import { Descriptions, Table, TableColumnsType } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  createFacility,
  deleteFacility,
  getFacilities,
  updateFacilitySeq,
} from '../../api/homepageManagementFacility.api';
import Button from '../../components/Button';
import ErrorResponse from '../../components/ErrorResponse';
import FileUpload from '../../components/FileUpload';
import ShowPreviewModal from '../../components/homepage/facility/modals/ShowPreviewModal';
import SimpleHeader from '../../components/layout/SimpleHeader';
import Loader from '../../components/Loader';
import SelectCampus from '../../components/SelectCampus';
import useAuth from '../../hooks/useAuth';
import useAxios from '../../hooks/useAxios';
import useNotification from '../../hooks/useNotification';
import {
  IFacility,
  IGetFacilities,
} from '../../models/homepageManagementFacility.model';
import { RootState } from '../../store/rootReducer';
import Split from 'react-split';

const Block = styled.div`
  .table-custom {
    margin-top: 20px;
  }

  .button {
    .disable > img {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const HomepageFacility = () => {
  const { t, i18n } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { isDarkMode } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // HP0004 = 시설 구분
  const HP0004 = commonCodes.find(item => item.commonCode === 'HP0004')?.detailCode;  
  const { successNotification, infoNotification } = useNotification();
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetFacilities>();
  const {
    data: deleteRes,
    isLoading: deleteIsLoading,
    error: deleteError,
    execute: deleteExecute,
  } = useAxios();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();

  const [facilityGbn, setFacilityGbn] = useState('');
  const [facilities, setFacilities] = useState<IFacility[]>([]);
  const [showPreviewVisible, setShowPreviewVisible] = useState(false);
  const [file, setFile] = useState<File>();

  useEffect(() => {
    if (currentCampus) {
      setFacilityGbn('');
    }
  }, [currentCampus]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      execute(getFacilities(currentCampus, facilityGbn));
      if (file) setFile(undefined);
      successNotification(t('common.message.save1'));
    }
  }, [submitRes, submitIsLoading]);

  const onSelectFacilityGbn = (code: string) => {
    setFacilityGbn(code);
    execute(getFacilities(currentCampus, code));
  };

  useEffect(() => {
    if (res && !isLoading) {
      setFacilities(sortItems(res.data));
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      execute(getFacilities(currentCampus, facilityGbn));
      successNotification(t('common.message.delete'));
    }
  }, [deleteRes, deleteIsLoading]);

  const sortItems = (items: IFacility[]) => {
    items.sort((a, b) => (a.seq < b.seq ? -1 : a.seq > b.seq ? 1 : 0));
    items.forEach((item, index) => {
      item.seq = index + 1;
    });

    return items;
  };

  const raiseItem = (seq: number) => {
    const items = [...facilities];
    const index = items.findIndex((item) => item.seq === seq);
    items[index].seq--;
    items[index - 1].seq++;
    setFacilities(sortItems(items));
  };

  const lowerItem = (seq: number) => {
    const items = [...facilities];
    const index = items.findIndex((item) => item.seq === seq);
    items[index].seq++;
    items[index + 1].seq--;
    setFacilities(sortItems(items));
  };

  const onRemoveItem = (campusFacilityCode: number) => {
    deleteExecute(deleteFacility(currentCampus, campusFacilityCode));
  };

  const onSubmitSeq = () => {
    submitExecute(updateFacilitySeq(currentCampus, facilityGbn, facilities));
  };

  const onSubmitFile = () => {
    if (!file) {
      return infoNotification(t('homepage.facility.message1'));
    }

    const form = new FormData();
    form.append('facilityGbn', facilityGbn);
    form.append('facilityFile', file);
    submitExecute(createFacility(currentCampus, form));
  };

  const columns: TableColumnsType<IFacility> = [
    {
      title: t('system.menu.column1'),
      dataIndex: 'seq',
      key: 'seq',
      align: 'center',
      sorter: (a, b) => a.seq - b.seq,
    },
    {
      title: t('common.column.file'),
      key: 'facilityFile',
      dataIndex: 'facilityFile',
      align: 'center',
      render: (value) => (
        <a
          className="btn-text"
          href={`${value?.fileLocation}`}
          download={value?.originalName}
        >
          {value?.originalName}
        </a>
      ),
    },
    {
      title: t('system.menu.column4'),
      key: 'seq',
      dataIndex: 'seq',
      align: 'center',
      render: (value) => (
        <div className="order-sett__area">
          <a
            className={value === 1 ? 'btn-order up disabled' : 'btn-order up'}
            onClick={() => {
              if (value === 1) return;
              raiseItem(value);
            }}
          />
          <a
            className={value === facilities.length ? 'btn-order down disabled' : 'btn-order down'}
            onClick={() => {
              if (value === facilities.length) return;
              lowerItem(value);
            }}
          />
        </div>
      ),
    },
    {
      title: t('common.button.delete'),
      key: 'campusFacilityCode',
      dataIndex: 'campusFacilityCode',
      align: 'center',
      render: (value) => (
        <div className='comm-btn__area justify-content-center'>
          <Button
            className="btn-basic small gray"
            onClick={() => onRemoveItem(value)}
            text={t('common.button.delete')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      ),
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-homepage-big.svg"
      parentMenu={t('common.menu.homepage')}
      currentMenu={t('homepage.facility.title1')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <SelectCampus />
          </li>
        </ul>
      </div>
    </div>
    <Block className="content">
      {isLoading || deleteIsLoading || submitIsLoading ? (
        <Loader />
      ) : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={deleteError} />
      <ErrorResponse error={submitError} />

      <Split
        className="splite-divide"
        sizes={[25, 75]}
        minSize={100}
        expandToMin={false}
        gutterSize={10}
        gutterAlign="center"
        snapOffset={30}
        dragInterval={1}
        direction="horizontal"
        cursor="col-resize"
      >
        <div className="col-frame left_panel">
          {/* left side */}
          <div className='comm-table__area'>
            <table>
              <thead>
                <tr>
                  <th>{t('homepage.facility.column1')}</th>
                </tr>
              </thead>
              <tbody>
                {HP0004?.map(item => (
                  <tr
                    key={item.detailCode}
                    className={facilityGbn === item.detailCode ? 'chosen' : 'pointer'}
                    onClick={() => onSelectFacilityGbn(item.detailCode)}
                  >
                    <td>{i18n.language === 'ko' ? item.korName : item.engName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-frame right_panel">
          {/* right side */}
          {facilityGbn ? (
            <div>
              <Table
                className="comm-table__area"
                columns={columns}
                dataSource={facilities}
                pagination={{
                  pageSize: 99999,
                }}
                rowKey="campusFacilityCode"
              />
              <div className="comm-bx__space">
                <div className="comm-btn__area right">
                  <Button
                    className="btn-basic pink"
                    onClick={onSubmitSeq}
                    text={t('system.menu.button2')}
                    writeAuthYn={writeAuthYn}
                  />
                  <Button
                    className="btn-basic pink-line"
                    onClick={() => setShowPreviewVisible(true)}
                    text={t('homepage.facility.button1')}
                  />
                </div>
              </div>
              <div className="comm-table__area rowType">
              <Descriptions
                bordered
                size="small"
                column={1}
                labelStyle={{ width: '25%' }}
              >
                <Descriptions.Item label={t('homepage.facility.column2')}>
                  <div className="flex flex-justify-between">
                    <FileUpload
                      file={file}
                      setFile={setFile}
                      accept=".png, .bmp, .jpg, .jpeg, .gif"
                    />
                    <div className='comm-btn__area'>
                      <Button
                        className="btn-basic pink"
                        onClick={onSubmitFile}
                        text={t('common.button.save')}
                        writeAuthYn={writeAuthYn}
                      />
                    </div>
                  </div>
                </Descriptions.Item>
              </Descriptions>
              </div>
            </div>
          ) : null}
        </div>
        <ShowPreviewModal
          visible={showPreviewVisible}
          setVisible={setShowPreviewVisible}
          facilityGbn={facilityGbn}
          facilities={facilities}
        />
      </Split>
    </Block>
    </>
  );
};

export default HomepageFacility;
